import React, { useEffect, useRef, useState } from 'react';

interface DocumentInfo {
  id: string;
  name: string;
  size: number;
}

interface ContextStats {
  estimatedTokens: number;
  totalCharacters: number;
  reportIncluded: boolean;
  documentsIncluded: DocumentInfo[];
  chatHistoryMessages: number;
}

interface ChatContextViewerProps {
  contextStats?: ContextStats;
  reportContent?: string;
  documents?: Array<{ id: string; name: string }>;
  includedDocuments?: Set<string>;
  pastReports?: Array<{ id: string; title: string }>;
  includedPastReports?: Set<string>;
  chatHistory?: Array<{ role: string; content: string }>;
  onToggleReport?: (enabled: boolean) => void;
  onToggleDocument?: (docId: string, enabled: boolean) => void;
  onTogglePastReport?: (reportId: string, enabled: boolean) => void;
  onToggleChatHistory?: (enabled: boolean) => void;
  className?: string;
}

const formatTokens = (tokens: number) => {
  if (tokens >= 1000) return `${(tokens / 1000).toFixed(1)}K`;
  return `${tokens}`;
};

/**
 * Compact "Sources" picker that replaces the previous "AI Context — Show
 * What's Sent to AI" expanding panel. Renders as a chip on the composer:
 *
 *     Sources: Report + 3 docs   ▾   1.2K tok
 *
 * Click the chip to open a small dropdown with one checkbox per source.
 * Token estimate (when known) sits next to the chip as a passive indicator.
 */
export const ChatContextViewer: React.FC<ChatContextViewerProps> = ({
  contextStats,
  reportContent,
  documents = [],
  includedDocuments,
  pastReports = [],
  includedPastReports,
  chatHistory = [],
  onToggleReport,
  onToggleDocument,
  onTogglePastReport,
  onToggleChatHistory,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  // Report and chat history default to "on"; we don't track an explicit
  // boolean state here because the parent owns the include/exclude logic.
  const [reportOn, setReportOn] = useState(true);
  const [historyOn, setHistoryOn] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Build the summary string for the chip label.
  const reportPart = reportContent && reportOn ? 'Report' : null;
  const docCount = includedDocuments ? includedDocuments.size : documents.length;
  const docsPart = docCount > 0 ? `${docCount} doc${docCount === 1 ? '' : 's'}` : null;
  const pastCount = includedPastReports ? includedPastReports.size : 0;
  const pastPart = pastCount > 0 ? `${pastCount} past` : null;
  const historyPart = chatHistory.length > 0 && historyOn ? 'history' : null;
  const summary = [reportPart, docsPart, pastPart, historyPart].filter(Boolean).join(' + ') || 'None';

  const tokenWarning = contextStats
    ? contextStats.estimatedTokens > 900000
      ? 'error'
      : contextStats.estimatedTokens > 700000
      ? 'warn'
      : 'ok'
    : 'ok';
  const tokenChipClass =
    tokenWarning === 'error'
      ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-700'
      : tokenWarning === 'warn'
      ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-700'
      : 'bg-gray-50 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300 border-gray-200 dark:border-gray-600';

  return (
    <div ref={rootRef} className={`relative inline-flex items-center gap-2 ${className}`}>
      {/* Main chip — opens the picker */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-700 dark:text-gray-200 hover:border-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
        title="Choose what's sent to the AI"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.414-1.414A1 1 0 0012.586 5H7a2 2 0 00-2 2v4" />
        </svg>
        <span>Sources: <span className="font-medium">{summary}</span></span>
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Token estimate — passive indicator */}
      {contextStats && (
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium ${tokenChipClass}`}
          title="Estimated token usage for the next message"
        >
          ~{formatTokens(contextStats.estimatedTokens)} tok
        </span>
      )}

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute z-30 top-full left-0 mt-2 w-80 max-h-[60vh] overflow-y-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3"
          role="listbox"
        >
          <div className="text-[11px] uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-2">
            What's sent to the AI
          </div>

          {/* Report */}
          {reportContent && (
            <label className="flex items-start gap-2 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-2 -mx-2">
              <input
                type="checkbox"
                checked={reportOn}
                onChange={(e) => {
                  setReportOn(e.target.checked);
                  onToggleReport?.(e.target.checked);
                }}
                className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-800 dark:text-gray-100">This report</div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                  {(reportContent.length / 1000).toFixed(1)}K chars
                </div>
              </div>
            </label>
          )}

          {/* Documents */}
          {documents.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] text-gray-500 dark:text-gray-400 px-2 mb-1">
                Documents
              </div>
              {documents.map((doc) => {
                const checked = includedDocuments ? includedDocuments.has(doc.id) : false;
                return (
                  <label
                    key={doc.id}
                    className="flex items-start gap-2 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-2 -mx-2"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => onToggleDocument?.(doc.id, e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800 dark:text-gray-100 truncate" title={doc.name}>
                        {doc.name}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {/* Past reports */}
          {pastReports.length > 0 && (
            <div className="mt-2">
              <div className="text-[11px] text-gray-500 dark:text-gray-400 px-2 mb-1">
                Past reports
              </div>
              {pastReports.map((r) => {
                const checked = includedPastReports ? includedPastReports.has(r.id) : false;
                return (
                  <label
                    key={r.id}
                    className="flex items-start gap-2 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-2 -mx-2"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => onTogglePastReport?.(r.id, e.target.checked)}
                      className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-800 dark:text-gray-100 truncate" title={r.title}>
                        {r.title}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {/* Chat history */}
          {chatHistory.length > 0 && (
            <div className="mt-2">
              <label className="flex items-start gap-2 py-1.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 rounded px-2 -mx-2">
                <input
                  type="checkbox"
                  checked={historyOn}
                  onChange={(e) => {
                    setHistoryOn(e.target.checked);
                    onToggleChatHistory?.(e.target.checked);
                  }}
                  className="mt-0.5 h-4 w-4 text-blue-600 rounded border-gray-300"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-800 dark:text-gray-100">
                    Conversation history
                  </div>
                  <div className="text-[11px] text-gray-500 dark:text-gray-400">
                    {chatHistory.length} message{chatHistory.length === 1 ? '' : 's'}
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Token warning */}
          {contextStats && tokenWarning !== 'ok' && (
            <div
              className={`mt-3 text-[11px] px-2 py-1.5 rounded border ${tokenChipClass}`}
            >
              {tokenWarning === 'error'
                ? `High token usage — consider trimming sources.`
                : `Approaching the context limit.`}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatContextViewer;
